import React, { useState, useEffect, useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaSearch, FaUser, FaBuilding, FaPlus, FaMinus, FaTrash, FaTimes, FaFile } from 'react-icons/fa';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import OrganizationalUnitService from '../services/OrganizationalUnitService';
import PageService from '../services/PageService';
import AccessControlService from '../services/AccessControlService';
import OrganizationalUnitTypeService from '../services/OrganizationalUnitTypeService';
import UserPositionService from '../services/UserPositionService';
import ProfileImage from '../components/ProfileImage';
import '../assets/styles/AccessControl.css';
import { getIconComponent } from './IconPicker';

// Internal Modal component
const AccessControlModal = ({ isOpen, title, onClose, children, footer }) => {
    if (!isOpen) return null;
  
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="access-modal-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="access-modal-content"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="access-modal-header">
                            <h3 className="access-modal-title">{title}</h3>
                            <button className="access-modal-close" onClick={onClose}>
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="access-modal-body">
                            {children}
                        </div>
                        
                        {footer && (
                            <div className="access-modal-footer">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const AccessControl = ({ darkMode }) => {
    // State for organizational units and pages
    const [orgUnits, setOrgUnits] = useState([]);
    const [pages, setPages] = useState([]);
    const [expandedUnits, setExpandedUnits] = useState({});
    const [expandedPages, setExpandedPages] = useState({});
    const [unitSearch, setUnitSearch] = useState('');
    const [pageSearch, setPageSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageAccessInfo, setPageAccessInfo] = useState({});
    
    // State for access control modal
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({
        type: '', // 'user' or 'unit'
        sourceId: null,
        sourceName: '',
        targetId: null,
        targetName: '',
        accessTypes: [],
        selectedAccessType: null,
        exclusion: false,
        exclusions: [], // Array to store selected exclusions
        childrenToExclude: [] // Array to store children that can be excluded
    });
    
    // State for suggestions
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    
    // State for page access controls
    const [pageAccessControls, setPageAccessControls] = useState({});

    // State for exclusion search
    const [exclusionSearch, setExclusionSearch] = useState('');
    const [exclusionSuggestions, setExclusionSuggestions] = useState([]);
    const [showExclusionSuggestions, setShowExclusionSuggestions] = useState(false);

    // Add useEffect to handle theme changes
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    // Fetch organizational units, pages, and access types on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Fetch organizational units
                const unitsData = await OrganizationalUnitService.getOrganizationChart();
                if (!unitsData || unitsData.length === 0) {
                    setError('No organizational units found');
                    setLoading(false);
                    return;
                }
                setOrgUnits(unitsData);
                
                // Fetch pages
                const pagesData = await PageService.getAll();
                setPages(pagesData);
                
                // Initialize expanded states
                const unitExpanded = {};
                const initializeExpandedState = (units) => {
                    units.forEach(unit => {
                        unitExpanded[unit.id] = false;
                        if (unit.children && unit.children.length > 0) {
                            initializeExpandedState(unit.children);
                        }
                    });
                };
                initializeExpandedState(unitsData);
                setExpandedUnits(unitExpanded);
                
                const pageExpanded = {};
                pagesData.forEach(page => {
                    pageExpanded[page.id] = false;
                });
                setExpandedPages(pageExpanded);
                
                // After pages are set, fetch access controls
                if (pagesData && pagesData.length > 0) {
                    await fetchAllAccessControls();
                }
                
                setLoading(false);
            } catch (err) {
                setError('Failed to load data. Please try again later.');
                setLoading(false);
                toast.error('Failed to load data. Please try again later.');
            }
        };
        
        fetchData();
    }, []);

    // Add effect to update access info when pages change
    useEffect(() => {
        if (pages && pages.length > 0) {
            fetchAllAccessControls();
        }
    }, [pages]);

    // Fetch access controls for all pages
    const fetchAllAccessControls = async () => {
        try {
            const accessInfoByPage = {};
            
            // Function to recursively process pages and their children
            const processPages = async (pagesList) => {
                for (const page of pagesList) {
                    try {
                        const pageAccess = await AccessControlService.getPageAccessInfo(page.id);
                        if (pageAccess) {
                            accessInfoByPage[page.id] = pageAccess;
                        }
                        
                        // Process child pages if they exist
                        if (page.children && page.children.length > 0) {
                            await processPages(page.children);
                        }
                    } catch (err) {
                        toast.error(`Failed to fetch access info for page ${page.name}`);
                    }
                }
            };

            if (pages && pages.length > 0) {
                await processPages(pages);
                setPageAccessInfo(accessInfoByPage);
            }
        } catch (err) {
            toast.error('Failed to fetch access controls');
        }
    };
    
    // Toggle expanded state for units
    const toggleUnitExpand = (unitId) => {
        setExpandedUnits(prev => ({
            ...prev,
            [unitId]: !prev[unitId]
        }));
    };
    
    // Toggle expanded state for pages
    const togglePageExpand = (pageId) => {
        setExpandedPages(prev => ({
            ...prev,
            [pageId]: !prev[pageId]
        }));
    };
    
    // Filter units based on search term
    const filterUnits = (units, searchTerm) => {
        if (!searchTerm) return units;
        
        return units.filter(unit => {
            const matchesName = unit.name.toLowerCase().includes(searchTerm.toLowerCase());
            const hasMatchingChildren = unit.children && filterUnits(unit.children, searchTerm).length > 0;
            const hasMatchingUsers = unit.assignedUsers && unit.assignedUsers.some(user => 
                user.userFullName.toLowerCase().includes(searchTerm.toLowerCase())
            );
            
            return matchesName || hasMatchingChildren || hasMatchingUsers;
        });
    };
    
    // Filter pages based on search term
    const filterPages = (pages, searchTerm) => {
        if (!searchTerm) return pages;
        
        return pages.filter(page => {
            const matchesName = page.name.toLowerCase().includes(searchTerm.toLowerCase());
            const hasMatchingChildren = page.children && filterPages(page.children, searchTerm).length > 0;
            
            return matchesName || hasMatchingChildren;
        });
    };
    
    // Handle drag end event
    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        
        if (!destination) {
            return;
        }
        
        const draggedItemType = draggableId.split('-')[0];
        const draggedItemId = draggableId.split('-')[1];
        
        if (destination.droppableId.startsWith('page-')) {
            const pageId = destination.droppableId.replace('page-', '');
            
            const findPageName = (pages, id) => {
                const idStr = String(id);
                const idNum = Number(id);
                
                for (const page of pages) {
                    if (String(page.id) === idStr || Number(page.id) === idNum) return page.name;
                    if (page.children && page.children.length > 0) {
                        const childResult = findPageName(page.children, id);
                        if (childResult) return childResult;
                    }
                }
                return null;
            };
            
            const pageName = findPageName(pages, pageId);
            
            if (draggedItemType === 'unit') {
                const findUnitName = (units, id) => {
                    const idStr = String(id);
                    const idNum = Number(id);
                    
                    for (const unit of units) {
                        if (String(unit.id) === idStr || Number(unit.id) === idNum) return unit.name;
                        if (unit.children && unit.children.length > 0) {
                            const childResult = findUnitName(unit.children, id);
                            if (childResult) return childResult;
                        }
                    }
                    return null;
                };
                
                const unitName = findUnitName(orgUnits, draggedItemId);
                
                setModalData({
                    type: 'unit',
                    sourceId: draggedItemId,
                    sourceName: unitName || `Unit ${draggedItemId}`,
                    targetId: pageId,
                    targetName: pageName || `Page ${pageId}`,
                    accessTypes: [],
                    selectedAccessType: null,
                    exclusion: false,
                    exclusions: [],
                    childrenToExclude: []
                });
                setShowModal(true);
                setExclusionSearch('');
                
                fetchUnitChildren(draggedItemId);
                
                try {
                    const accessTypes = await AccessControlService.getPageAccessTypes(pageId);
                    setModalData(prev => ({
                        ...prev,
                        accessTypes: accessTypes || []
                    }));
                } catch (err) {
                    toast.error('Failed to fetch access types. Please try again.');
                }
            } else if (draggedItemType === 'user') {
                // Find user name
                const findUserName = (units, userId) => {
                    // Convert id to both string and number for comparison
                    const userIdStr = String(userId);
                    const userIdNum = Number(userId);
                    
                    for (const unit of units) {
                        // Check both assignedUsers and users properties
                        const unitUsers = unit.assignedUsers || unit.users || [];
                        
                        // Find user by userId or id, comparing both string and number versions
                        const user = unitUsers.find(u => 
                            String(u.userId) === userIdStr || 
                            Number(u.userId) === userIdNum ||
                            String(u.id) === userIdStr || 
                            Number(u.id) === userIdNum
                        );
                        
                        if (user) {
                            return user.userFullName || user.fullName || user.name || 'Unknown User';
                        }
                        
                        // Check children recursively
                        if (unit.children && unit.children.length > 0) {
                            const name = findUserName(unit.children, userId);
                            if (name) return name;
                        }
                    }
                    return 'Unknown User';
                };
                
                const userName = findUserName(orgUnits, draggedItemId);
                
                // Open modal for user access
                setModalData({
                    type: 'user',
                    sourceId: draggedItemId,
                    sourceName: userName || `User ${draggedItemId}`,
                    targetId: pageId,
                    targetName: pageName || `Page ${pageId}`,
                    accessTypes: [], // Initialize with empty array
                    selectedAccessType: null,
                    exclusion: false,
                    exclusions: [],
                    childrenToExclude: []
                });
                setShowModal(true); // Show modal immediately
                setExclusionSearch(''); // Reset exclusion search
                
                // Fetch access types for the modal
                try {
                    const accessTypes = await AccessControlService.getPageAccessTypes(pageId);
                    setModalData(prev => ({
                        ...prev,
                        accessTypes: accessTypes || [] // Ensure it's an array even if null/undefined
                    }));
                } catch (err) {
                    toast.error('Failed to fetch access types. Please try again.');
                }
            }
        }
    };
    
    // Handle access assignment
    const handleAssignAccess = async () => {
        try {
            // Only check for access type if there are access types available
            if (modalData.accessTypes.length > 0 && !modalData.selectedAccessType) {
                toast.error('Please select an access type');
                return;
            }

            // Check if access already exists
            const pageAccess = pageAccessInfo[modalData.targetId];
            if (pageAccess) {
                if (modalData.type === 'unit') {
                    // Check if unit already has access
                    const unitExists = pageAccess.unitAccesses?.some(unit => unit.unitId === parseInt(modalData.sourceId));
                    if (unitExists) {
                        toast.error('This unit already has access to this page');
                        return;
                    }
                } else {
                    // Check if user already has access
                    const userExists = pageAccess.userAccesses?.some(user => user.userId === parseInt(modalData.sourceId));
                    if (userExists) {
                        toast.error('This user already has access to this page');
                        return;
                    }
                }
            }

            // Prepare access data according to AccessControlCreateDTO
            const accessData = {
                pageId: parseInt(modalData.targetId),
                // Only include accessPageTypeId if there are access types and one is selected
                ...(modalData.accessTypes.length > 0 && modalData.selectedAccessType && {
                    accessPageTypeId: parseInt(modalData.selectedAccessType)
                }),
                isExcluded: modalData.exclusion || false,
                excludedFromUnitId: modalData.exclusion && modalData.type === 'unit' ? parseInt(modalData.sourceId) : null,
                userId: modalData.type === 'user' ? parseInt(modalData.sourceId) : null,
                organizationalUnitId: modalData.type === 'unit' ? parseInt(modalData.sourceId) : null
            };

            // Call API to assign access
            let result;
            if (modalData.type === 'unit') {
                result = await AccessControlService.addUnitAccess(accessData);
            } else {
                result = await AccessControlService.addUserAccess(accessData);
            }

            // Process exclusions if any
            if (modalData.exclusions && modalData.exclusions.length > 0) {
                for (const exclusion of modalData.exclusions) {
                    const exclusionData = {
                        pageId: parseInt(modalData.targetId),
                        // Only include accessPageTypeId if there are access types and one is selected
                        ...(modalData.accessTypes.length > 0 && modalData.selectedAccessType && {
                            accessPageTypeId: parseInt(modalData.selectedAccessType)
                        }),
                        isExcluded: true,
                        excludedFromUnitId: modalData.type === 'unit' ? parseInt(modalData.sourceId) : null,
                        userId: exclusion.type === 'user' ? parseInt(exclusion.id) : null,
                        organizationalUnitId: exclusion.type === 'unit' ? parseInt(exclusion.id) : null
                    };

                    if (exclusion.type === 'unit') {
                        await AccessControlService.addUnitAccess(exclusionData);
                    } else {
                        await AccessControlService.addUserAccess(exclusionData);
                    }
                }
            }

            // Refresh all page access controls
            await fetchAllAccessControls();

            // Close modal and show success message
            setShowModal(false);
            toast.success('Access assigned successfully');
        } catch (err) {
            toast.error('Failed to assign access. Please try again.');
        }
    };
    
    // Render organizational unit tree
    const renderUnitTree = (units, level = 0) => {
        if (!units || units.length === 0) return null;
        
        const filteredUnits = filterUnits(units, unitSearch);
        
        if (filteredUnits.length === 0) return null;
        
        return (
            <ul className="unit-tree" style={{ paddingLeft: level > 0 ? '20px' : '0' }}>
                {filteredUnits.map((unit, index) => {
                    // Check for users in the unit data (could be in assignedUsers or users property)
                    const unitUsers = unit.assignedUsers || unit.users || [];
                    
                    return (
                        <li key={`unit-${unit.id}`} className="unit-item">
                            <div className="unit-header">
                                <button 
                                    className="toggle-btn"
                                    onClick={() => toggleUnitExpand(unit.id)}
                                >
                                    {expandedUnits[unit.id] ? <FaMinus /> : <FaPlus />}
                                </button>
                                <Draggable draggableId={`unit-${unit.id}`} index={index} key={`unit-${unit.id}`}>
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className="unit-label"
                                            style={{
                                                ...provided.draggableProps.style,
                                                backgroundColor: unit.unitTypeColor || '#3498db'
                                            }}
                                        >
                                            <span style={{ marginRight: '5px' }}>
                                                {getIconComponent(unit.unitTypeIcon) || <FaBuilding />}
                                            </span>
                                            {unit.name}
                                        </div>
                                    )}
                                </Draggable>
                            </div>
                            
                            {expandedUnits[unit.id] && (
                                <>
                                    {unitUsers && unitUsers.length > 0 ? (
                                        <ul className="user-list">
                                            {unitUsers.map((user, userIndex) => (
                                                <div key={`user-${user.userId || user.id}`}>
                                                    <Draggable draggableId={`user-${user.userId || user.id}`} index={userIndex}>
                                                        {(provided) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className="user-label"
                                                            >
                                                                <FaUser style={{ marginRight: '5px' }} />
                                                                {user.userFullName || user.fullName || user.name || 'Unknown User'}
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                </div>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="no-users-message">No users assigned to this unit</div>
                                    )}
                                    
                                    {unit.children && unit.children.length > 0 && renderUnitTree(unit.children, level + 1)}
                                </>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    };
    
    // Render page tree
    const renderPageTree = (pages, level = 0) => {
        if (!pages || pages.length === 0) return null;
        
        const filteredPages = filterPages(pages, pageSearch);
        
        if (filteredPages.length === 0) return null;
        
        return (
            <ul className="page-tree" style={{ paddingLeft: level > 0 ? '20px' : '0' }}>
                {filteredPages.map((page, index) => (
                    <li key={`page-${page.id}`} className="page-item">
                        <div className="page-header">
                            {page.children && page.children.length > 0 && (
                                <button 
                                    className="toggle-btn"
                                    onClick={() => togglePageExpand(page.id)}
                                >
                                    {expandedPages[page.id] ? <FaMinus /> : <FaPlus />}
                                </button>
                            )}
                            
                            <Droppable droppableId={`page-${page.id}`}>
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={`page-label ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                                    >
                                        <div className="page-title-row">
                                            <span className="page-icon">
                                                {getPageIcon(page.iconCode)}
                                            </span>
                                            <span className="page-name">
                                                {page.name}
                                            </span>
                                        </div>
                                        
                                        {/* Display page access information */}
                                        {pageAccessInfo[page.id] && (
                                            <div className="page-access-info">
                                                {/* User Access Section */}
                                                {pageAccessInfo[page.id].userAccesses.map(userAccess => (
                                                    <div key={userAccess.accessControlId} className="access-item user-access">
                                                        <div className="access-header">
                                                            <ProfileImage 
                                                                imageUrl={userAccess.imageUrl}
                                                                width={24}
                                                                height={24}
                                                            />
                                                            <span className="access-name">{userAccess.fullName || userAccess.username || 'Unknown User'}</span>
                                                            <span className="access-type">{userAccess.accessTypeName}</span>
                                                            <button 
                                                                className="delete-btn"
                                                                onClick={() => handleRemoveAccess(userAccess.accessControlId)}
                                                            >
                                                                <FaTimes />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Unit Access Section */}
                                                {pageAccessInfo[page.id].unitAccesses.map(unitAccess => (
                                                    <div key={unitAccess.accessControlId} className="access-item unit-access">
                                                        <div className="access-header" style={{ backgroundColor: unitAccess.unitTypeColor }}>
                                                            <span className="unit-icon">
                                                                {getIconComponent(unitAccess.unitTypeIcon)}
                                                            </span>
                                                            <span className="access-name">{unitAccess.unitName}</span>
                                                            <span className="access-type">{unitAccess.accessTypeName}</span>
                                                            <button 
                                                                className="delete-btn"
                                                                onClick={() => handleRemoveAccess(unitAccess.accessControlId)}
                                                            >
                                                                <FaTimes />
                                                            </button>
                                                        </div>

                                                        {/* Exclusions Section */}
                                                        {unitAccess.exclusions.length > 0 && (
                                                            <div className="exclusions-section">
                                                                {unitAccess.exclusions.map(exclusion => (
                                                                    <div key={exclusion.accessControlId} className="exclusion-item">
                                                                        {exclusion.isUnitExclusion ? (
                                                                            // Unit Exclusion
                                                                            <div className="exclusion-content unit-exclusion">
                                                                                <span className="exclusion-name">
                                                                                    {exclusion.name}
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            // User Exclusion
                                                                            <div className="exclusion-content user-exclusion">
                                                                                <ProfileImage 
                                                                                    imageUrl={`/api/users/${exclusion.userId}/image`}
                                                                                    width={20}
                                                                                    height={20}
                                                                                />
                                                                                <span className="exclusion-name">
                                                                                    {exclusion.name}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        <button 
                                                                            className="delete-exclusion-btn"
                                                                            onClick={() => handleRemoveAccess(exclusion.accessControlId)}
                                                                        >
                                                                            <FaTimes />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                        
                        {expandedPages[page.id] && page.children && page.children.length > 0 && renderPageTree(page.children, level + 1)}
                    </li>
                ))}
            </ul>
        );
    };
    
    // Handle removing access
    const handleRemoveAccess = async (accessId) => {
        try {
            await AccessControlService.removeAccess(accessId);
            
            // Refresh all page access controls
            await fetchAllAccessControls();
            
            toast.success('Access removed successfully');
        } catch (err) {
            toast.error('Failed to remove access. Please try again.');
        }
    };
    
    // Function to search users/units
    const handleSearch = async (searchTerm, type) => {
        if (!searchTerm.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            let results = [];
            if (type === 'user') {
                // Search in the current organizational structure for users
                const searchInUnits = (units, term) => {
                    let found = [];
                    units.forEach(unit => {
                        const unitUsers = unit.assignedUsers || unit.users || [];
                        found = found.concat(
                            unitUsers.filter(user => 
                                (user.userFullName || user.fullName || user.name || '')
                                .toLowerCase()
                                .includes(term.toLowerCase())
                            )
                        );
                        if (unit.children && unit.children.length > 0) {
                            found = found.concat(searchInUnits(unit.children, term));
                        }
                    });
                    return found;
                };
                results = searchInUnits(orgUnits, searchTerm);
            } else {
                // Search in the current organizational structure for units
                const searchInUnits = (units, term) => {
                    let found = [];
                    units.forEach(unit => {
                        if (unit.name.toLowerCase().includes(term.toLowerCase())) {
                            found.push(unit);
                        }
                        if (unit.children && unit.children.length > 0) {
                            found = found.concat(searchInUnits(unit.children, term));
                        }
                    });
                    return found;
                };
                results = searchInUnits(orgUnits, searchTerm);
            }
            
            setSuggestions(results);
            setShowSuggestions(true);
        } catch (error) {
            toast.error('Error searching. Please try again.');
        }
    };
    
    // Function to handle suggestion selection
    const handleSuggestionSelect = (suggestion) => {
        setModalData(prev => ({
            ...prev,
            sourceId: suggestion.id || suggestion.userId,
            sourceName: suggestion.name || suggestion.userFullName || suggestion.fullName
        }));
        setShowSuggestions(false);
    };

    // Function to fetch children of a unit for exclusion
    const fetchUnitChildren = async (unitId) => {
        try {
            // Get all children units
            const unitResponse = await OrganizationalUnitService.getUnitById(unitId);
            const childUnits = unitResponse.children || [];
            
            // Get all users in the unit and its child units using our endpoint
            const users = await OrganizationalUnitService.getUsersByUnitId(unitId);
            
            // Format the data for display
            const formattedUnits = childUnits.map(unit => ({
                id: unit.id,
                name: unit.name,
                type: 'unit'
            }));
            
            const formattedUsers = users.map(user => ({
                id: user.id,
                name: user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user.username || 'Unknown User',
                type: 'user'
            }));
            
            // Combine units and users
            const children = [...formattedUnits, ...formattedUsers];
            
            setModalData(prev => ({
                ...prev,
                childrenToExclude: children
            }));
            
            return children;
        } catch (err) {
            toast.error('Failed to fetch unit children');
            return [];
        }
    };

    // Function to handle exclusion search
    const handleExclusionSearch = (searchTerm) => {
        setExclusionSearch(searchTerm);
        
        if (!searchTerm) {
            setExclusionSuggestions([]);
            setShowExclusionSuggestions(false);
            return;
        }
        
        const filteredSuggestions = modalData.childrenToExclude.filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
            !modalData.exclusions.some(exclusion => exclusion.id === item.id && exclusion.type === item.type)
        );
        
        setExclusionSuggestions(filteredSuggestions);
        setShowExclusionSuggestions(true);
    };

    // Function to add an exclusion
    const addExclusion = (item) => {
        setModalData(prev => ({
            ...prev,
            exclusions: [...prev.exclusions, item]
        }));
        setExclusionSearch('');
        setShowExclusionSuggestions(false);
    };

    // Function to remove an exclusion
    const removeExclusion = (id, type) => {
        setModalData(prev => ({
            ...prev,
            exclusions: prev.exclusions.filter(item => !(item.id === id && item.type === type))
        }));
    };
    
    // Function to get the appropriate icon based on iconCode
    const getPageIcon = (iconCode) => {
        // Use the IconPicker's getIconComponent function for consistent icon handling
        const icon = getIconComponent(iconCode);
        // If no icon is found, return a default file icon
        return icon || <FaFile />;
    };
    
    // Function to handle modal close
    const handleModalClose = () => {
        setShowModal(false);
        setExclusionSearch('');
        setExclusionSuggestions([]);
        setShowExclusionSuggestions(false);
        // Reset modal data
        setModalData(prev => ({
            ...prev,
            selectedAccessType: null,
            exclusion: false,
            exclusions: []
        }));
    };
    
    if (loading) {
        return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;
    }
    
    if (error) {
        return <div className="error">{error}</div>;
    }
    
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="access-control-container">
                <div className="access-control-content">
                    {/* Organizational Units Section */}
                    <div className="access-control-section units-section">
                        <h2 className="access-control-section-title">Organizational Units</h2>
                        <div className="search-container">
                            <input
                                type="text"
                                placeholder="Search units or users..."
                                value={unitSearch}
                                onChange={(e) => setUnitSearch(e.target.value)}
                                className="search-input"
                            />
                            <FaSearch className="search-icon" />
                        </div>
                        <Droppable droppableId="units-container" isDropDisabled={true}>
                            {(provided) => (
                                <div 
                                    className="tree-container"
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {orgUnits && orgUnits.length > 0 ? (
                                        renderUnitTree(orgUnits)
                                    ) : (
                                        <div className="no-data-message">No organizational units found</div>
                                    )}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                    
                    {/* Pages Section */}
                    <div className="access-control-section pages-section">
                        <h2 className="access-control-section-title">Pages</h2>
                        <div className="search-container">
                            <input
                                type="text"
                                placeholder="Search pages..."
                                value={pageSearch}
                                onChange={(e) => setPageSearch(e.target.value)}
                                className="search-input"
                            />
                            <FaSearch className="search-icon" />
                        </div>
                        <div className="tree-container">
                            {pages && pages.length > 0 ? (
                                renderPageTree(pages)
                            ) : (
                                <div className="no-data-message">No pages found</div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Access Control Modal */}
                {showModal && (
                    <AccessControlModal
                        isOpen={showModal}
                        title={modalData.type === 'unit' ? "Assign Unit Access" : "Assign User Access"}
                        onClose={handleModalClose}
                        footer={
                            <div className="access-modal-footer">
                                <button className="btn btn-secondary" onClick={handleModalClose}>
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={handleAssignAccess}
                                    disabled={modalData.accessTypes.length > 0 && !modalData.selectedAccessType}
                                >
                                    Assign Access
                                </button>
                            </div>
                        }
                    >
                        <div className="access-modal-form">
                            {/* Assignment Information */}
                            <div className="modal-selection-info">
                                <h3 className="assignment-title">Access Assignment</h3>
                                <div className="assignment-details">
                                    <div className="assignment-item">
                                        <span className="assignment-label">From:</span>
                                        <span className="assignment-value">
                                            {modalData.type === 'unit' ? (
                                                <><FaBuilding style={{ marginRight: '5px' }} /> {modalData.sourceName}</>
                                            ) : (
                                                <><FaUser style={{ marginRight: '5px' }} /> {modalData.sourceName}</>
                                            )}
                                        </span>
                                    </div>
                                    <div className="assignment-arrow">→</div>
                                    <div className="assignment-item">
                                        <span className="assignment-label">To:</span>
                                        <span className="assignment-value">
                                            <FaFile style={{ marginRight: '5px' }} /> {modalData.targetName}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Access Type Selection */}
                            {modalData.accessTypes.length > 0 && (
                                <div className="form-group">
                                    <label>Access Type:</label>
                                    <select
                                        value={modalData.selectedAccessType || ''}
                                        onChange={(e) => setModalData(prev => ({
                                            ...prev,
                                            selectedAccessType: e.target.value
                                        }))}
                                    >
                                        <option value="">Select an access type</option>
                                        {modalData.accessTypes.map(type => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            {/* Exclusions Section - Only show for units */}
                            {modalData.type === 'unit' && modalData.childrenToExclude.length > 0 && (
                                <div className="form-group exclusions-section">
                                    <h4 className="exclusions-title">Exclude Children from Access</h4>
                                    <div className="exclusions-explanation">
                                        <p>When you grant access to <strong>{modalData.sourceName}</strong>, all users and sub-units within it inherit that access.</p>
                                        <p>Use this section to select specific children that should <strong>NOT</strong> receive this access permission.</p>
                                    </div>
                                    
                                    <div className="search-input-container">
                                        <label>Search for children to exclude:</label>
                                        <input
                                            type="text"
                                            value={exclusionSearch}
                                            onChange={(e) => handleExclusionSearch(e.target.value)}
                                            placeholder="Type to search for users or sub-units..."
                                            className="search-input"
                                        />
                                        {showExclusionSuggestions && exclusionSuggestions.length > 0 && (
                                            <div className="suggestions-container">
                                                {exclusionSuggestions.map((suggestion) => (
                                                    <div
                                                        key={`${suggestion.type}-${suggestion.id}`}
                                                        className="suggestion-item"
                                                        onClick={() => addExclusion(suggestion)}
                                                    >
                                                        {suggestion.type === 'user' ? (
                                                            <><FaUser style={{ marginRight: '5px' }} />
                                                            {suggestion.name}</>
                                                        ) : (
                                                            <><FaBuilding style={{ marginRight: '5px' }} />
                                                            {suggestion.name}</>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Selected Exclusions */}
                                    {modalData.exclusions.length > 0 && (
                                        <div className="selected-exclusions">
                                            <label>Selected Exclusions:</label>
                                            <ul className="exclusions-list">
                                                {modalData.exclusions.map((item) => (
                                                    <li key={`${item.type}-${item.id}`} className="exclusion-item">
                                                        {item.type === 'user' ? (
                                                            <><FaUser style={{ marginRight: '5px' }} />
                                                            {item.name}</>
                                                        ) : (
                                                            <><FaBuilding style={{ marginRight: '5px' }} />
                                                            {item.name}</>
                                                        )}
                                                        <button 
                                                            className="remove-exclusion-btn"
                                                            onClick={() => removeExclusion(item.id, item.type)}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </AccessControlModal>
                )}
            </div>
        </DragDropContext>
    );
};

export default AccessControl; 